/**
 * MemoryDashboard — `/memory` page.
 *
 * Permission model:
 *   - READ open to any authenticated user (browse rules / facts / episodes
 *     / digest as a Detect engineer-curious teammate).
 *   - WRITE (promote / retire / archive) gated to the single admin email.
 *     Backend enforces this on every POST; the FE additionally hides
 *     the action buttons for non-admins via the ``canEdit`` prop threaded
 *     through to each tab. A small banner at the top tells non-admins
 *     they're in read-only mode so they don't wonder where the buttons
 *     went.
 *
 * Layout (top-to-bottom):
 *   1. Page header
 *   2. Read-only banner (non-admins only)
 *   3. Collapsible digest panel (latest improvement digest, rendered as
 *      markdown when present, "No digest yet" when null).
 *   4. Three MUI Tabs:
 *        - Rules     (proposed + active sub-tables, promote/edit/retire)
 *        - Facts     (table grouped by topic with collapsible groups)
 *        - Episodes  (search + filters, archive)
 *
 * All data fetching happens in the tab components themselves — keeping
 * the parent thin means switching tabs is instant after the first load.
 */

import {
  Alert,
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState, type ReactElement } from 'react';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, memory } from '@/helpers/ApiHelper';
import type { ImprovementDigestOut } from '@/types/memory';

import { NarrativeMarkdown } from '@/components/NarrativeMarkdown';

import { CaptureGapsTab } from './tabs/CaptureGapsTab';
import { EpisodesTab } from './tabs/EpisodesTab';
import { FactsTab } from './tabs/FactsTab';
import { RulesTab } from './tabs/RulesTab';

type ActiveTab = 'rules' | 'facts' | 'episodes' | 'capture_gaps';

const TAB_LABELS: Record<ActiveTab, string> = {
  rules: 'Rules',
  facts: 'Facts',
  episodes: 'Episodes',
  capture_gaps: 'Capture gaps',
};

export function MemoryDashboard(): ReactElement {
  const [tab, setTab] = useState<ActiveTab>('rules');
  const { user } = useAuth();
  const canEdit = isAdminEmail(user?.email);

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'primary.main',
            textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
          }}
        >
          memory
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What Sherlock has learned, and what it knows.
        </Typography>
      </Stack>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }} role="status">
          Read-only view. Promote, retire, and archive actions are restricted
          to the admin (mohit@detectinspections.com).
        </Alert>
      )}

      <DigestPanel />

      <Tabs
        value={tab}
        onChange={(_, v: ActiveTab) => setTab(v)}
        aria-label="Memory section"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {(Object.keys(TAB_LABELS) as ActiveTab[]).map((key) => (
          <Tab key={key} label={TAB_LABELS[key]} value={key} />
        ))}
      </Tabs>

      {tab === 'rules' && (
        <Box role="tabpanel" aria-label="Rules panel">
          <RulesTab canEdit={canEdit} />
        </Box>
      )}
      {tab === 'facts' && (
        <Box role="tabpanel" aria-label="Facts panel">
          <FactsTab canEdit={canEdit} />
        </Box>
      )}
      {tab === 'episodes' && (
        <Box role="tabpanel" aria-label="Episodes panel">
          <EpisodesTab canEdit={canEdit} />
        </Box>
      )}
      {tab === 'capture_gaps' && (
        <Box role="tabpanel" aria-label="Capture gaps panel">
          <CaptureGapsTab canEdit={canEdit} />
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Digest panel
// ---------------------------------------------------------------------------

function DigestPanel(): ReactElement {
  const [digest, setDigest] = useState<ImprovementDigestOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await memory.getLatestDigest();
        if (!cancelled) setDigest(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.detail : 'Failed to load digest');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasContent = digest?.markdown != null && digest.markdown.length > 0;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Latest improvement digest
          </Typography>
          {digest?.generated_at && (
            <Typography variant="caption" color="text.secondary">
              generated {new Date(digest.generated_at).toLocaleString()}
            </Typography>
          )}
        </Stack>
        <IconButton
          size="small"
          aria-label={open ? 'Collapse digest' : 'Expand digest'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          {!error && !digest && (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          )}
          {!error && digest && !hasContent && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No digest yet — the weekly job runs Mondays at 09:30 UTC.
              Trigger one manually with{' '}
              <code>POST /api/sync/memory/improvement-digest/</code>.
            </Typography>
          )}
          {hasContent && digest && <NarrativeMarkdown text={digest.markdown ?? ''} />}
        </CardContent>
      </Collapse>
    </Card>
  );
}
